import { Injectable } from '@angular/core';
import { QuestionnaireRequest } from 'src/API';
import { DryEyeMedicalHistoryFormGroup } from 'src/app/shared/consult-forms/dry-eye-forms/dry-eye-medical-history-form/dry-eye-medical-history-form.model';
import {
  MedicalHistoryPopulator,
  MedicalHistoryPopulatorKey,
  MedicalHistorySource
} from './medical-history-source.model';
import { MedicationReconciler } from './medication/medication-reconciler';
import {
  CustomQuestionnairePopulator,
  DewsIiiPopulator
} from './populators/autopopulate-config.populator';
import { DerfsPopulator } from './populators/derfs.populator';
import { IntakeFormPopulator } from './populators/intake-form.populator';
import { NonModifiablePopulator } from './populators/non-modifiable.populator';
import { SpeedPopulator } from './populators/speed.populator';
import { QuestionnaireSourceClassifier } from './questionnaire/questionnaire-source.classifier';

/**
 * The single entry point for populating the dry-eye medical history form. Given
 * a list of sources it dispatches each to the populator registered under its
 * `populatorKey`, then reconciles medications exactly once. To understand or
 * debug what fills a field, start here.
 */
@Injectable({ providedIn: 'root' })
export class MedicalHistoryLinkingService {
  private readonly registry: { [K in MedicalHistoryPopulatorKey]: MedicalHistoryPopulator };

  constructor(
    nonModifiablePopulator: NonModifiablePopulator,
    intakeFormPopulator: IntakeFormPopulator,
    derfsPopulator: DerfsPopulator,
    customQuestionnairePopulator: CustomQuestionnairePopulator,
    dewsIiiPopulator: DewsIiiPopulator,
    speedPopulator: SpeedPopulator,
    private classifier: QuestionnaireSourceClassifier,
    private reconciler: MedicationReconciler
  ) {
    this.registry = {
      nonModifiable: nonModifiablePopulator,
      intakeForm: intakeFormPopulator,
      derfs: derfsPopulator,
      custom: customQuestionnairePopulator,
      dewsIII: dewsIiiPopulator,
      speed: speedPopulator
    };
  }

  populateFromSources(
    formGroup: DryEyeMedicalHistoryFormGroup,
    sources: MedicalHistorySource[]
  ): void {
    sources.forEach(source => {
      if (source && source.data && this.registry[source.populatorKey]) {
        this.registry[source.populatorKey].populate(formGroup, source.data);
      }
    });
    this.reconciler.reconcile(formGroup);
    formGroup.markAsDirty();
  }

  populateFromLinkedQuestionnaires(
    formGroup: DryEyeMedicalHistoryFormGroup,
    questionnaires: QuestionnaireRequest[]
  ): void {
    this.populateFromSources(formGroup, this.classifier.classifyLinked(questionnaires));
  }

  getLatestCompletedDerfs(questionnaires: QuestionnaireRequest[]): QuestionnaireRequest {
    return this.classifier.getLatestCompletedDerfs(questionnaires);
  }
}
